import apiClient from '@/lib/axios';
import type {
  Lead,
  LeadFormData,
  LeadsQueryParams,
  ApiResponse,
  UserReference,
  UserLookupResponse,
  InsuranceCompanyReference,
  InsuranceAdjustorReference,
  ReferralSourceReference,
} from '../types';

/**
 * Transforms form data for API submission.
 * Converts empty strings to null for optional fields (especially dates).
 */
const prepareLeadDataForApi = (data: LeadFormData): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    // Convert empty strings to null for optional fields
    if (value === '') {
      result[key] = null;
    } else {
      result[key] = value;
    }
  }

  return result;
};

/**
 * Get list of leads with optional filters
 */
export const getLeads = async (params?: LeadsQueryParams): Promise<Lead[]> => {
  const response = await apiClient.get<ApiResponse<Lead[]>>('/leads', { params });
  return response.data.data;
};

/**
 * Get a single lead by ID
 */
export const getLead = async (id: number): Promise<Lead> => {
  const response = await apiClient.get<ApiResponse<Lead>>(`/leads/${id}`);
  return response.data.data;
};

/**
 * Create a new lead
 */
export const createLead = async (data: LeadFormData): Promise<Lead> => {
  const apiData = prepareLeadDataForApi(data);
  const response = await apiClient.post<ApiResponse<Lead>>('/leads', apiData);
  return response.data.data;
};

/**
 * Update an existing lead
 */
export const updateLead = async (id: number, data: LeadFormData): Promise<Lead> => {
  const apiData = prepareLeadDataForApi(data);
  const response = await apiClient.put<ApiResponse<Lead>>(`/leads/${id}`, apiData);
  return response.data.data;
};

/**
 * Delete a lead
 */
export const deleteLead = async (id: number): Promise<void> => {
  await apiClient.delete(`/leads/${id}`);
};

/**
 * Assign lead to an estimator
 */
export const assignLead = async (id: number, userId: number): Promise<Lead> => {
  const response = await apiClient.put<ApiResponse<Lead>>(`/leads/${id}/assign`, {
    assignedToUserId: userId,
  });
  return response.data.data;
};

/**
 * Update lead status (legacy - use transitionLeadStatus instead)
 */
export const updateLeadStatus = async (id: number, status: string): Promise<Lead> => {
  const response = await apiClient.put<ApiResponse<Lead>>(`/leads/${id}/status`, {
    status,
  });
  return response.data.data;
};

/**
 * Transition lead to a new status using the status transition API
 * This handles business logic like job number generation for Sold status
 * and requires closing comments for NoGo/Closed statuses
 */
export const transitionLeadStatus = async (
  id: number,
  newStatus: string,
  closingComments?: string,
  archiveJob?: boolean
): Promise<Lead> => {
  const response = await apiClient.put<ApiResponse<Lead>>(`/leads/${id}/status-transition`, {
    newStatus,
    closingComments,
    archiveJob,
  });
  return response.data.data;
};

/**
 * Get list of users (for estimator dropdown)
 */
export const getUsers = async (): Promise<UserReference[]> => {
  const response = await apiClient.get<ApiResponse<UserLookupResponse[]>>('/users/lookup');
  return response.data.data.map((user) => ({
    id: user.id,
    name: user.fullName,
  }));
};

/**
 * Get list of insurance companies for dropdown (uses lookup endpoint)
 */
export const getInsuranceCompanies = async (): Promise<InsuranceCompanyReference[]> => {
  const response = await apiClient.get<ApiResponse<InsuranceCompanyReference[]>>(
    '/insurance-companies/lookup'
  );
  return response.data.data;
};

interface AdjustorLookupResponse {
  id: number;
  fullName: string;
  insuranceCompanyId: number;
  companyName: string;
}

/**
 * Get list of insurance adjustors for dropdowns (all active adjustors)
 */
export const getInsuranceAdjustors = async (): Promise<InsuranceAdjustorReference[]> => {
  const response = await apiClient.get<ApiResponse<AdjustorLookupResponse[]>>(
    '/insurance-adjustors/lookup'
  );
  return response.data.data.map((adjustor) => ({
    id: adjustor.id,
    name: adjustor.fullName,
    companyId: adjustor.insuranceCompanyId,
    companyName: adjustor.companyName,
  }));
};

/**
 * Get list of referral sources
 */
export const getReferralSources = async (): Promise<ReferralSourceReference[]> => {
  const response = await apiClient.get<ApiResponse<ReferralSourceReference[]>>(
    '/referral-sources'
  );
  return response.data.data;
};

/**
 * Get list of US states
 */
export const getStates = (): { code: string; name: string }[] => {
  return [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
  ];
};

/**
 * Get damage categories
 */
export const getDamageCategories = (): string[] => {
  return ['Fire', 'Water', 'Mold', 'Storm', 'Vandalism', 'Other'];
};
